/**
 * A task list opened in the Tasks app: inline title, quick-add, open tasks in
 * sort order, then this session's completions grayed at the bottom
 * (Vikunja-style: reload or Clear removes them; the dashboard keeps history
 * forever). Read-only mirrors NoteView: viewer role, disconnected shared
 * space, or hard lock.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Eye, WifiOff } from "lucide-react";
import { createTaskIn, deleteTask, docName, moveTask, newId, setTaskDone, updateTask, type Recurrence, type TaskItem } from "@hydra-notes/shared";
import { resolveItem, useItem, useSpaceOf } from "@/data/store";
import { useTaskList } from "@/data/taskHooks";
import { useFolderMeta } from "@/data/sharedFolders";
import { useSyncState } from "@/data/sync";
import { useAuth } from "@/data/auth";
import { useSessionDone } from "@/store/sessionDone";
import { scheduleAchievementCheck } from "@/data/taskStats";
import { toast } from "@/ui/toast";
import { Button } from "@/ui/Button";
import { useTaskFocus } from "@/commands/tasks";
import { TaskQuickAdd, type QuickAddFields } from "./TaskQuickAdd";
import { TaskRow, type DropPos } from "./TaskRow";
import { TaskDetail } from "./TaskDetail";
import { celebrateComplete, celebrateListZero } from "./celebrate";
import { useFlip } from "./useFlip";
import { useDocPresence } from "@/data/presence";
import { PresenceChips } from "@/ui/PresenceChips";
import s from "./TaskListView.module.css";

const TASK_MIME = "application/x-hydra-task";
const EMPTY_PILE: string[] = [];

export function TaskListView({ listId }: { listId: string }) {
  const item = useItem(listId);
  const space = useSpaceOf(listId);
  if (!item || !space) return <div className={s.loading}>Task list not found</div>;
  return <ListInner key={listId} listId={listId} spaceId={space.spaceId} shared={space.kind === "shared"} />;
}

function ListInner({ listId, spaceId, shared }: { listId: string; spaceId: string; shared: boolean }) {
  const item = useItem(listId);
  const folderMeta = useFolderMeta(shared ? spaceId : null);
  const syncStatus = useSyncState((st) => st.status);
  const { tasks, loaded, handle } = useTaskList(listId, spaceId);
  const present = useDocPresence(spaceId, docName.task(spaceId, listId));
  const pile = useSessionDone((st) => st.piles[listId]) ?? EMPTY_PILE;
  const userId = useAuth((st) => st.user?.id) ?? "local";
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ overId: string; pos: DropPos } | null>(null);
  const dragId = useRef<string | null>(null);

  const isViewer = shared && folderMeta?.role === "viewer";
  const disconnected = shared && syncStatus !== "synced" && syncStatus !== "syncing";
  const readOnly = isViewer || disconnected || (shared && !!folderMeta?.locked);

  // remote and local completions both enter the session pile (a teammate's
  // checkmark sinks instead of the row blinking out). Remote flips are
  // ignored for a short settle window after load: the server catch-up on a
  // stale IndexedDB copy replays completions from other devices, and those
  // are history, not "this session".
  useEffect(() => {
    if (!handle) return;
    const openedAt = Date.now();
    return handle.store.onDoneTransition((t) => {
      if (!t.local && Date.now() - openedAt < 3_000) return;
      const sd = useSessionDone.getState();
      if (t.done) sd.add(listId, t.taskId);
      else sd.remove(listId, t.taskId);
    });
  }, [handle, listId]);

  // agenda-row clicks land here: scroll to the task and flash it once
  const focusTaskId = useTaskFocus((st) => st.taskId);
  useEffect(() => {
    if (!focusTaskId || !loaded) return;
    if (!tasks.some((t) => t.id === focusTaskId)) return;
    useTaskFocus.getState().consume();
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-task-id="${focusTaskId}"]`);
      if (!el) return;
      el.scrollIntoView({ block: "center" });
      el.classList.add(s.rowFlash!);
      setTimeout(() => el.classList.remove(s.rowFlash!), 1200);
    });
  }, [focusTaskId, loaded, tasks]);

  const open = useMemo(() => tasks.filter((t) => !t.done), [tasks]);
  const sunk = useMemo(() => {
    const byId = new Map(tasks.map((t) => [t.id, t]));
    return pile.map((id) => byId.get(id)).filter((t): t is TaskItem => !!t && t.done);
  }, [tasks, pile]);

  const orderKey = [...open.map((t) => t.id), "|", ...sunk.map((t) => t.id)].join(",");
  const flipRef = useFlip(orderKey);

  const touchList = () => resolveItem(listId)?.ctx.workspace.touch(listId);

  const addTask = (fields: QuickAddFields) => {
    if (!handle || readOnly) return;
    createTaskIn(
      handle.doc,
      newId(),
      { title: fields.title, dueDate: fields.dueDate, dueTime: fields.dueTime, recurrence: fields.recurrence },
      userId,
    );
    touchList();
  };

  const toggleDone = (task: TaskItem, el: Element | null) => {
    if (!handle || readOnly) return;
    const next = !task.done;
    setTaskDone(handle.doc, task.id, next, userId);
    touchList();
    if (next) {
      celebrateComplete(el);
      scheduleAchievementCheck();
      if (handle.store.getSnapshot().every((t) => t.done)) {
        celebrateListZero();
        toast.success("List clear! Everything is done.");
      }
    }
  };

  const updateFields = (
    taskId: string,
    fields: { description?: string; dueDate?: string | null; dueTime?: string | null; recurrence?: Recurrence | null },
  ) => {
    if (!handle || readOnly) return;
    updateTask(handle.doc, taskId, (m) => {
      if (fields.description !== undefined) {
        if (fields.description) m.set("description", fields.description);
        else m.delete("description");
      }
      if (fields.dueDate !== undefined) {
        if (fields.dueDate) m.set("dueDate", fields.dueDate);
        else {
          m.delete("dueDate");
          m.delete("dueTime");
        }
      }
      if (fields.dueTime !== undefined) {
        if (fields.dueTime) m.set("dueTime", fields.dueTime);
        else m.delete("dueTime");
      }
      if (fields.recurrence !== undefined) {
        if (fields.recurrence) m.set("recurrence", { ...fields.recurrence });
        else m.delete("recurrence");
      }
    });
    touchList();
  };

  const renameTask = (taskId: string, title: string) => {
    if (!handle || readOnly) return;
    updateTask(handle.doc, taskId, (m) => m.set("title", title));
    touchList();
  };

  const removeTask = (taskId: string) => {
    if (!handle || readOnly) return;
    const task = getTask0(tasks, taskId);
    deleteTask(handle.doc, taskId);
    useSessionDone.getState().remove(listId, taskId);
    setExpandedId((cur) => (cur === taskId ? null : cur));
    touchList();
    toast.success(`Deleted "${task?.title || "Untitled task"}"`);
  };

  // ---------- drag reorder (open tasks only) ----------

  const onDragStart = (taskId: string) => (e: React.DragEvent) => {
    dragId.current = taskId;
    e.dataTransfer.setData(TASK_MIME, JSON.stringify({ listId, taskId }));
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (taskId: string) => (e: React.DragEvent) => {
    if (!dragId.current || dragId.current === taskId) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const pos: DropPos = e.clientY < rect.top + rect.height / 2 ? "above" : "below";
    setDrag((cur) => (cur?.overId === taskId && cur.pos === pos ? cur : { overId: taskId, pos }));
  };

  const onDrop = (taskId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    const from = dragId.current;
    const pos = drag?.pos;
    setDrag(null);
    dragId.current = null;
    if (!handle || readOnly || !from || from === taskId || !pos) return;
    const idx = open.findIndex((t) => t.id === taskId);
    if (idx === -1) return;
    const beforeId = pos === "above" ? taskId : (open[idx + 1]?.id ?? undefined);
    if (beforeId === from) return;
    moveTask(handle.doc, from, beforeId);
    touchList();
  };

  const onDragEnd = () => {
    setDrag(null);
    dragId.current = null;
  };

  if (!item) return <div className={s.loading}>Task list not found</div>;

  const colorVar = item.colorKey ? `var(--tl-${item.colorKey})` : "var(--accent)";
  const expandedTask = expandedId ? (getTask0(tasks, expandedId) ?? null) : null;

  return (
    <div className={s.view} data-task-list-view style={{ "--list-accent": colorVar } as React.CSSProperties}>
      {isViewer ? (
        <div className={s.banner}>
          <Eye size={13} /> Read only: you have viewer access in "{folderMeta?.title}"
        </div>
      ) : disconnected ? (
        <div className={s.banner}>
          <WifiOff size={13} /> Reconnecting... shared task lists pause while offline
        </div>
      ) : null}

      <div className={s.inner}>
        <div className={s.header}>
          <span className={s.colorDot} aria-hidden />
          <input
            className={s.listTitle}
            placeholder="Untitled list"
            defaultValue={item.title}
            key={`${listId}:${item.title === "" ? "empty" : "t"}`}
            readOnly={readOnly}
            onBlur={(e) => {
              if (readOnly) return;
              const res = resolveItem(listId);
              if (res && e.target.value !== item.title) res.ctx.workspace.rename(listId, e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
          <PresenceChips present={present} verb="is here" writingVerb="is editing" />
          <span className={s.count}>
            {open.length === 0 ? "All done" : `${open.length} open`}
          </span>
        </div>

        <div className={s.quickAdd}>
          <TaskQuickAdd onAdd={addTask} disabled={readOnly} />
        </div>

        {!loaded ? (
          <div className={s.loading}>Loading tasks...</div>
        ) : open.length === 0 && sunk.length === 0 ? (
          <div className={s.empty}>
            <CheckCircle2 size={28} />
            <span>Nothing to do. Add a task above.</span>
          </div>
        ) : (
          <div className={s.rows} role="list">
            {open.map((t) => (
              <div key={t.id} role="listitem" className={s.rowWrap}>
                <TaskRow
                  task={t}
                  readOnly={readOnly}
                  expanded={expandedId === t.id}
                  sunk={false}
                  dropPos={drag?.overId === t.id ? drag.pos : null}
                  flipRef={flipRef(t.id)}
                  onToggleDone={(el) => toggleDone(t, el)}
                  onToggleExpand={() => setExpandedId((cur) => (cur === t.id ? null : t.id))}
                  onRename={(title) => renameTask(t.id, title)}
                  onDelete={() => removeTask(t.id)}
                  onDragStart={onDragStart(t.id)}
                  onDragOver={onDragOver(t.id)}
                  onDrop={onDrop(t.id)}
                  onDragEnd={onDragEnd}
                />
                {expandedId === t.id && expandedTask ? (
                  <TaskDetail
                    task={expandedTask}
                    readOnly={readOnly}
                    onUpdate={(f) => updateFields(t.id, f)}
                    onDelete={() => removeTask(t.id)}
                  />
                ) : null}
              </div>
            ))}

            {sunk.length > 0 ? (
              <>
                <div className={s.sunkHeader}>
                  <span>Completed this session ({sunk.length})</span>
                  <Button variant="ghost" size="sm" onClick={() => useSessionDone.getState().clearList(listId)}>
                    Clear
                  </Button>
                </div>
                {sunk.map((t) => (
                  <div key={t.id} role="listitem" className={s.rowWrap}>
                    <TaskRow
                      task={t}
                      readOnly={readOnly}
                      expanded={expandedId === t.id}
                      sunk
                      dropPos={null}
                      flipRef={flipRef(t.id)}
                      onToggleDone={(el) => toggleDone(t, el)}
                      onToggleExpand={() => setExpandedId((cur) => (cur === t.id ? null : t.id))}
                      onRename={() => {}}
                      onDelete={() => removeTask(t.id)}
                      onDragStart={() => {}}
                      onDragOver={() => {}}
                      onDrop={() => {}}
                      onDragEnd={() => {}}
                    />
                    {expandedId === t.id && expandedTask ? (
                      <TaskDetail
                        task={expandedTask}
                        readOnly={readOnly}
                        onUpdate={(f) => updateFields(t.id, f)}
                        onDelete={() => removeTask(t.id)}
                      />
                    ) : null}
                  </div>
                ))}
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function getTask0(tasks: TaskItem[], id: string): TaskItem | undefined {
  return tasks.find((t) => t.id === id);
}
